import React, { useState, useEffect, useRef } from 'react';
import { Coins, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useProfile } from '../context/DataContext';
import { VirtualCurrencyService } from '../services/VirtualCurrencyService';
import { COIN_BALANCE_CHANGED } from '../lib/coinEvents';

interface Profile {
  full_name: string;
  username: string;
  avatar_url: string | null;
}

interface HeaderProps {}

export const Header: React.FC = () => {
  const { user, logout, isAdmin, isTechnical, authInitialized } = useUser();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [coinBalance, setCoinBalance] = useState<number>(0);
  const { profile } = useProfile();
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (user) {
      loadCoinBalance();
    }
  }, [user]);

  useEffect(() => {
    const handler = async () => {
      const currentUser = userRef.current;
      if (!currentUser) return;
      try {
        const coinService = new VirtualCurrencyService();
        const userCoins = await coinService.getUserCoins(currentUser.id);
        setCoinBalance(Math.round(userCoins?.balance || 0));
      } catch (error) {
        console.error('Error loading coin balance:', error);
      }
    };
    window.addEventListener(COIN_BALANCE_CHANGED, handler);
    return () => window.removeEventListener(COIN_BALANCE_CHANGED, handler);
  }, []);

  const loadCoinBalance = async () => {
    if (!user) return;
    try {
      const coinService = new VirtualCurrencyService();
      const userCoins = await coinService.getUserCoins(user.id);
      setCoinBalance(Math.round(userCoins?.balance || 0));
    } catch (error) {
      console.error('Error loading coin balance:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowMenu(false);
    navigate('/');
  };

  return (
    <header className="bg-[#1a0a2e]/90 backdrop-blur-xl border-b border-white/[0.08] sticky top-0 z-50 py-5">
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/" className="flex items-center gap-3 group md:mr-8">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-20 w-20 object-contain"
            />
            <span className="font-bold text-lg tracking-tight hidden sm:block">The Singing League</span>
          </Link>

          {user && (
            <nav className="hidden md:flex space-x-6">
              <Link to="/recordings" className="text-white/80 hover:text-white transition-colors font-medium">
                Recordings
              </Link>
              <Link to="/leaderboard" className="text-white/80 hover:text-white transition-colors font-medium">
                Leaderboard
              </Link>
              <Link to="/sing" className="text-white/80 hover:text-white transition-colors font-medium">
                Sing
              </Link>
              <Link to="/tournaments" className="text-white/80 hover:text-white transition-colors font-medium">
                Compete
              </Link>
              <Link to="/achievements" className="text-white/80 hover:text-white transition-colors font-medium">
                Achievements
              </Link>
              <Link to="/academy" className="text-white/80 hover:text-white transition-colors font-medium">
                Learn
              </Link>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!authInitialized ? (
            <div className="flex items-center gap-2 opacity-0">
              <div className="w-16 h-10"></div>
            </div>
          ) : user ? (
            <div className="flex items-center gap-3">
              <Link
                to="/purchase-coins"
                className="flex items-center space-x-2 bg-purple-900/60 hover:bg-purple-900/80 px-3 py-1.5 rounded-lg transition-colors border border-yellow-400/30"
                title="Your coin balance"
              >
                <Coins size={18} className="text-yellow-400" />
                <span className="text-white font-semibold">{coinBalance.toLocaleString()}</span>
              </Link>
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center space-x-2"
                >
                  <div className="flex items-center space-x-2 cursor-pointer">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="User"
                        className="w-9 h-9 rounded-full object-cover border-2 border-violet-400"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-600 border-2 border-violet-400 flex items-center justify-center">
                        <User size={18} className="text-white" />
                      </div>
                    )}
                    <div className="hidden md:block text-white">
                      <span>{profile?.full_name || profile?.username || 'Loading...'}</span>
                    </div>
                  </div>
                </button>
                {showMenu && (
                  <div
                    className="absolute right-0 mt-2 w-48 bg-[#1a0a2e] rounded-md shadow-lg py-1 z-20 border border-white/[0.1]"
                    onMouseLeave={() => setShowMenu(false)}
                  >
                    <Link to="/profile" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">Your Profile</Link>
                    <Link to="/referrals" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">Referrals</Link>
                    <Link to="/help" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">Help & Support</Link>
                    {isAdmin && (
                      <>
                        <Link to="/admin/dashboard" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">
                          Admin Dashboard
                        </Link>
                        <Link to="/admin/vocal-blueprint-debug" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">
                          Blueprint Debug
                        </Link>
                      </>
                    )}
                    {isTechnical && (
                      <Link to="/technical/debug" className="block px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]">
                        Technical Debug
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-white/90 hover:bg-white/[0.06]"
                    >
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/help"
                className="hidden sm:inline-flex px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
              >
                Support
              </Link>
              <Link
                to="/login"
                className="px-4 py-2.5 text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2.5 text-sm font-semibold bg-violet-600 hover:bg-violet-500 rounded-lg hover:shadow-lg hover:shadow-violet-500/20 transition-all duration-300"
              >
                Get Started
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};